import React from 'react';
import { SkeletonForm, Spinner } from '@parsina/framework';
import { ClinicalI18n, bffQuery } from '@parsina/framework/clinical';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { useTranslation } from '../../i18n';
import DoctorIdentityStep from './DoctorIdentityStep.jsx';
import DoctorForeignStep from './DoctorForeignStep.jsx';
import DoctorPrizeStep from './DoctorPrizeStep.jsx';

/**
 * The doctor half of «ثبت کلینیک جدید», for someone who said «من پزشک هستم».
 *
 * Two screens that belong to the DOCTOR panel — the نظام پزشکی lookup and the
 * prize wheel — rendered here so a doctor who arrives at the clinic panel first
 * is not sent to another app to become a doctor and then back to build a
 * clinic. They are the doctor panel's screens, not copies with clinic words on
 * them: same transport (the doctor BFF), same catalogue, same rules.
 *
 * ── Why the extra provider ─────────────────────────────────────────────────
 *
 * The words these two screens use (`auth.onboarding.*`, including the wheel's)
 * ship in the CLINICAL language pack, not this panel's. `ClinicalI18n` is that
 * pack's provider — the same one screens/Clinical mounts around the visit room
 * — and it renders nothing until its first load lands, so it is given a
 * fallback rather than being allowed to blank the wizard mid-flow.
 *
 * The language is this panel's: a clinic owner who switched the panel to
 * English does not meet a Persian step in the middle of registration.
 */

// Asked ONCE, right after the council step succeeds, because that is the moment
// the BFF has written the practitioner record the offer is decided from. It
// returns [] for almost every doctor and it is allowed to fail: a campaign
// service that is down must cost a doctor nothing more than a wheel they never
// knew about.
const MY_PRIZE_DRAWS = `
  query MyPrizeDraws {
    myPrizeDraws {
      id wheelCode title subtitle status
      segments { index label kind amount }
      segmentIndex kind amount smartVisits
      whatsappMessages baleMessages smsMessages retry
      balance { balance currency }
      endsAtMs
    }
  }
`;

/**
 * A wheel worth showing: not spun yet, and with enough slices to BE a wheel
 * (the kit draws nothing under two, so a one-slice campaign would render an
 * empty page with a button on it).
 */
export const offeredDraw = (draws) => (Array.isArray(draws) ? draws : []).find(
  (d) => d?.status === 'offered' && Array.isArray(d.segments) && d.segments.length >= 2,
) || null;

/**
 * The wheel this doctor is owed, or null. Never throws — see above.
 */
export async function fetchOfferedDraw() {
  try {
    const data = await bffQuery(MY_PRIZE_DRAWS);
    return offeredDraw(data?.myPrizeDraws);
  } catch {
    return null; /* the wheel is a bonus; onboarding is not */
  }
}

// Which «مشخصات پزشک» this account gets (PM-0061). The doctor BFF answers from
// its own record of the number the account signed in with: a non-Iranian one
// has no کد نظام پزشکی to look up, so that doctor states their identity in a
// form instead.
const ONBOARDING_CONTEXT = `
  query OnboardingContext {
    onboardingContext { foreign phone impliedLanguage }
  }
`;

/**
 * The identity step, once it is known which one.
 *
 * The COUNCIL form is what every failure falls back to — a doctor BFF older
 * than this panel, a network error, a number that says nothing. The foreign
 * form is only ever shown on a positive answer — or, under a brand outside the
 * Iranian market (`market: 'intl'`), always: there is no Iranian medical council
 * to look anyone up in, so nobody meets the council form.
 *
 * The language is NOT taken from this answer: the panel already runs in the
 * language the clinic BFF derived from the same number (`myProfile.language`,
 * PM-0060), and two sources for one setting is how they come to disagree.
 */
function DoctorIdentity({ onDone, onSkip, disabled }) {
  // `undefined` while the BFF is being asked; after that always an object.
  const [context, setContext] = React.useState(undefined);

  React.useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => bffQuery(ONBOARDING_CONTEXT))
      .then((data) => data?.onboardingContext || {})
      .catch(() => ({}))
      .then((ctx) => { if (live) setContext(ctx); });
    return () => { live = false; };
  }, []);

  if (context === undefined) {
    return <SkeletonForm fields={2} actions={1} data-testid="onboarding-doctor-loading" />;
  }
  return context.foreign || !isIranMarket()
    ? <DoctorForeignStep phone={context.phone} onDone={onDone} onSkip={onSkip} disabled={disabled} />
    : <DoctorIdentityStep onDone={onDone} onSkip={onSkip} disabled={disabled} />;
}

export default function DoctorTrack({ phase, draw, onIdentityDone, onSkip, onPrizeDone, disabled }) {
  const { locale } = useTranslation();

  return (
    <ClinicalI18n
      language={locale}
      fallback={(
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spinner size={20} />
        </div>
      )}
    >
      {phase === 'prize'
        ? <DoctorPrizeStep draw={draw} onDone={onPrizeDone} />
        : <DoctorIdentity onDone={onIdentityDone} onSkip={onSkip} disabled={disabled} />}
    </ClinicalI18n>
  );
}
